import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import { SessionService } from '../session.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private readonly baseUrl: string = window.location.origin.indexOf('localhost') !== -1
        ? 'http://localhost:55329/api/'
        : '/service/api/';

    private readonly jsonHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });

    constructor(
        private http: HttpClient,
        private router: Router,
        private sessionService: SessionService
    ) { }

    /**
     * Step 1 — Submits email and password.
     * Response may contain otpRequired: true, which means OTP must be validated next.
     */
    login(dsEmail: string, cdSenha: string): Observable<any> {
        return this.http.post(
            `${this.baseUrl}login`,
            { dsEmail, cdSenha },
            { headers: this.jsonHeaders }
        );
    }

    /**
     * Resend OTP — Requests a new OTP code without requiring the password again.
     * Uses the dedicated ReenviarOtp endpoint.
     */
    reenviarOtp(dsEmail: string): Observable<any> {
        return this.http.post(
            `${this.baseUrl}login/ReenviarOtp`,
            { dsEmail },
            { headers: this.jsonHeaders }
        );
    }

    /**
     * Step 2 — Validates the OTP code sent to the user's email.
     * On success, response contains autenticado: true and session data.
     */
    validarOtp(dsEmail: string, codigoOtp: string): Observable<any> {
        return this.http.post(
            `${this.baseUrl}login/ValidarOtp`,
            { dsEmail, codigoOtp },
            { headers: this.jsonHeaders }
        );
    }

    /**
     * Logs out the current session on the server, then clears the local session.
     */
    logout(): Observable<any> {
        const hash = this.getSessionHash();
        return this.http.post(
            `${this.baseUrl}login/Logout`,
            { hash },
            { headers: this.jsonHeaders }
        ).pipe(
            tap(
                () => this.clearSession(),
                () => this.clearSession()
            )
        );
    }

    /**
     * Logs out all active sessions for an email, then clears the local session.
     */
    logoutAll(dsEmail: string): Observable<any> {
        const hash = this.getSessionHash();
        return this.http.post(
            `${this.baseUrl}login/LogoutAll`,
            { dsEmail, hash },
            { headers: this.jsonHeaders }
        ).pipe(
            tap(
                () => this.clearSession(),
                () => this.clearSession()
            )
        );
    }

    /**
     * Persists the authenticated session received after OTP validation.
     * Integrates with the existing SessionService to maintain app-wide compatibility.
     */
    saveSession(result: { hash: string; nmUsuario: string; idUsuario: number; lstRelatorios: any[] }): void {
        this.sessionService.usuario.hash = result.hash;
        this.sessionService.usuario.nmUsuario = result.nmUsuario;
        this.sessionService.usuario.idUsuario = +result.idUsuario;
        this.sessionService.usuario.lstRelatorios = result.lstRelatorios || [];
        this.sessionService.usuario.nrRelatorios = this.sessionService.usuario.lstRelatorios.length;
        this.sessionService.fnSetSessao();
    }

    /**
     * Removes all session data from storage and resets the in-memory session state.
     */
    clearSession(): void {
        sessionStorage.removeItem('sessao_usuario');
        this.sessionService.usuario.hash = '';
        this.sessionService.usuario.nmUsuario = '';
        this.sessionService.usuario.idUsuario = 0;
        this.sessionService.usuario.lstRelatorios = [];
        this.sessionService.usuario.nrRelatorios = 0;
    }

    /** Returns the current session hash, or empty string if not authenticated. */
    getSessionHash(): string {
        if (this.sessionService.usuario.hash) {
            return this.sessionService.usuario.hash;
        }
        // Fallback: read directly from sessionStorage for guard checks before service init
        try {
            const raw = sessionStorage.getItem('sessao_usuario');
            if (raw) {
                const parsed = JSON.parse(raw);
                return (parsed && parsed.hash) ? parsed.hash : '';
            }
        } catch (e) { }
        return '';
    }

    /** Returns true if a valid session hash is present. */
    isAuthenticated(): boolean {
        return !!this.getSessionHash();
    }
}
